import React, { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import Box from '@mui/material/Box';

import { Spinner } from './components/ui-kit';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AccessDenied from './components/AccessDenied';
import InactiveUser from './components/InactiveUser';
import Auth from './pages/Auth';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import UsersList from './pages/UsersList';
import UserEdit from './pages/UserEdit';
import RolesList from './pages/RolesList';
import RoleEdit from './pages/RoleEdit';
import PermissionsList from './pages/PermissionsList';
import TokensList from './pages/TokensList';
import NotificationTemplatesList from './pages/NotificationTemplatesList';
import NotificationTemplateEdit from './pages/NotificationTemplateEdit';
import NotFound from './pages/NotFound';
import { NotificationsPageWidget } from './components/NotificationsWidget';
import { useAuth } from './context/AuthContext';
import { usePermissions } from './context/PermissionsContext';

function App() {
  const { isLoading, isAuthenticated, isUserActive } = useAuth();
  const { hasServiceAccess, hasPermission, isLoading: isPermissionsLoading } = usePermissions();

  // Memoize computed values to prevent unnecessary re-renders
  const memoizedValues = useMemo(() => ({
    hasAuthServiceAccess: hasServiceAccess('auth-service'),
    isUserActiveValue: isUserActive(),
    tokenPermission: hasPermission('auth-service', 'api-token', 'list')
      ? 'auth-service:api-token:list'
      : 'auth-service:api-token:list-own',
  }), [hasServiceAccess, hasPermission, isUserActive]);

  // Memoize the main content to prevent unnecessary re-renders
  const mainContent = useMemo(() => (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={<Auth />} />
      <Route
        path="/register"
        element={<Auth initialActivePanel="register" />}
      />
      <Route path="/inactive-user" element={<InactiveUser />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {isAuthenticated && !memoizedValues.isUserActiveValue ? (
              <InactiveUser />
            ) : isAuthenticated && !memoizedValues.hasAuthServiceAccess ? (
              <AccessDenied />
            ) : (
              <Layout />
            )}
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="notifications" element={<NotificationsPageWidget />} />

        <Route
          path="users"
          element={
            <ProtectedRoute requiredPermission="auth-service:user:list">
              <UsersList />
            </ProtectedRoute>
          }
        />

        <Route
          path="users/:id"
          element={
            <ProtectedRoute requiredPermission="auth-service:user:read">
              <UserEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="roles"
          element={
            <ProtectedRoute requiredPermission="auth-service:role:list">
              <RolesList />
            </ProtectedRoute>
          }
        />

        <Route
          path="roles/new"
          element={
            <ProtectedRoute requiredPermission="auth-service:role:create">
              <RoleEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="roles/:id"
          element={
            <ProtectedRoute requiredPermission="auth-service:role:read">
              <RoleEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="permissions"
          element={
            <ProtectedRoute requiredPermission="auth-service:permission:list">
              <PermissionsList />
            </ProtectedRoute>
          }
        />

        <Route
          path="tokens"
          element={
            <ProtectedRoute requiredPermission={memoizedValues.tokenPermission}>
              <TokensList />
            </ProtectedRoute>
          }
        />

        <Route
          path="notification-templates"
          element={
            <ProtectedRoute requiredPermission="auth-service:notification_template:list">
              <NotificationTemplatesList />
            </ProtectedRoute>
          }
        />

        <Route
          path="notification-templates/:id"
          element={
            <ProtectedRoute requiredPermission="auth-service:notification_template:read">
              <NotificationTemplateEdit />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 Page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  ), [isAuthenticated, memoizedValues.isUserActiveValue, memoizedValues.hasAuthServiceAccess, memoizedValues.tokenPermission]);
  

  if (isLoading || isPermissionsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
          opacity: 0.9,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <Spinner size={40} color="primary" />
      </Box>
    );
  }

  return mainContent;
}

export default App;
